import { useState, useCallback, useEffect, useMemo } from 'react';
import throttle from 'lodash.throttle';
import isEqual from 'lodash.isequal';
const ON_CHANGE_ACTIONS = {
  selectOption: 'select-option',
  createOption: 'create-option',
  clear: 'clear',
  removeValue: 'remove-value',
};

const ON_CHANGE_ACTIONS_ARRAY = Object.values(ON_CHANGE_ACTIONS);

const MENU_LIST_CLASSNAME = 'ws-ui-base-select__menu-list';

const THROTTLE_VALUE = 500;

const useSelect = ({
  defaultInputValue,
  isAutocomplete,
  isSearchable,
  onBlur,
  onFocus,
  onChange,
  onCreateOption,
  onInputChange,
  preventOverflow,
  defaultMenuIsOpen = false,
  controlledValue,
  controlledInputValue,
  openMenuOnFocus,
  menuIsOpen: controlledIsOpen,
  withDropdownIndicator,
}) => {
  const [isOpen, setIsOpen] = useState(defaultMenuIsOpen);

  const initializeInitialValue = () => {
    if (isAutocomplete) {
      if (defaultInputValue) {
        // Cast onCreateOption (which returns SelectOption) to SelectValue<IsMulti>.
        // This is safe because autocomplete always creates single options, never multi-value arrays.
        // TypeScript can't infer this narrowing automatically when IsMulti is generic,
        // so we explicitly tell it the runtime value is always a single SelectOption.
        return onCreateOption(defaultInputValue);
      }

      return null;
    }

    return undefined;
  };

  const initialValue = controlledValue ?? initializeInitialValue();

  const [value, setValue] = useState(initialValue);
  const [onChangeCallbackData, setOnChangeCallbackData] = useState(initialValue);
  const [inputValue, setInputValue] = useState(defaultInputValue || '');
  const [isBlurred, setIsBlurred] = useState(false);

  const onValueChange = useCallback(
    (newValue, options = {}) => {
      if (!isEqual(onChangeCallbackData, newValue) || options.forceChange) {
        onChange(newValue);
        setOnChangeCallbackData(newValue);
      }
    },
    [onChangeCallbackData, onChange]
  );

  const handleChange = (data, { action }) => {
    if (ON_CHANGE_ACTIONS_ARRAY.includes(action)) {
      onValueChange(data, { forceChange: isAutocomplete });
      setValue(data);

      if (isAutocomplete && !Array.isArray(data)) {
        const selected = data;
        const inpValue = typeof selected?.label === 'string' ? selected.label : '';
        setInputValue(inpValue);
      }
    }
  };

  const handleClear = () => {
    if (isAutocomplete) {
      setValue(null);
      setIsOpen(defaultMenuIsOpen);
      setInputValue('');
    }
  };

  const handleBlur = () => {
    setIsBlurred(true);
  };

  const handleFocus = () => {
    if (openMenuOnFocus && !isOpen) {
      setIsOpen(true);
    }

    onFocus();
  };

  const handleInputChange = useCallback(
    (inpValue, { action }) => {
      const isInputChangeAction = action === 'input-change';

      if (isInputChangeAction && isAutocomplete) {
        setInputValue(inpValue);
        setIsOpen(!!inpValue || defaultMenuIsOpen);

        if (inpValue === '') {
          setValue(null);
        } else {
          const data = onCreateOption(inpValue);
          // Cast to SelectValue<IsMulti> since onCreateOption always returns a single option,
          // but autocomplete mode technically supports any IsMulti value.
          // In practice, autocomplete is always used with IsMulti=false.
          setValue(data);
        }
      }

      if (isInputChangeAction && isSearchable) {
        onInputChange(inpValue);
      }
    },
    [defaultMenuIsOpen, isAutocomplete, isSearchable, onCreateOption, onInputChange]
  );

  const handleOpen = () => {
    if (withDropdownIndicator) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    if (controlledIsOpen === null) {
      setIsOpen(false);
    }
  };

  const handleCloseOnScroll = useCallback(
    (event) => {
      const target = event?.target;
      const isMenuList =
        target instanceof Element && target.classList.contains(MENU_LIST_CLASSNAME);

      if (preventOverflow && !isMenuList) {
        setIsOpen(false);
      }
    },
    [preventOverflow]
  );

  const throttledHandleCloseOnScroll = useMemo(
    () => throttle(handleCloseOnScroll, THROTTLE_VALUE),
    [handleCloseOnScroll]
  );

  const handleCloseMenuOnScroll = useCallback(
    (event) => {
      throttledHandleCloseOnScroll(event);
      return false;
    },
    [throttledHandleCloseOnScroll]
  );

  // we need such effect in order to trigger on change with updated value
  // after onChange which will happened in handleChange callback
  useEffect(() => {
    if (isBlurred) {
      setIsBlurred(false);

      onValueChange(value);
      onBlur();
    }
  }, [isBlurred, onBlur, onChange, value, onValueChange]);

  useEffect(() => {
    if (isAutocomplete && controlledInputValue != null && controlledInputValue !== inputValue) {
      handleInputChange(controlledInputValue, {
        action: 'input-change',
        prevInputValue: inputValue,
      });
    }
    // Intentionally depend only on controlledInputValue and inputValue:
    // this effect is only for isAutocomplete controlled input synchronization.
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [controlledInputValue, inputValue]);

  useEffect(() => {
    if (!controlledIsOpen && controlledIsOpen !== null) {
      setIsOpen(false);
    }
  }, [controlledIsOpen]);

  return {
    isOpen,
    value,
    inputValue,
    handleBlur,
    handleFocus,
    handleClear,
    handleChange,
    handleClose,
    handleInputChange,
    handleOpen,
    handleCloseOnScroll: handleCloseMenuOnScroll,
  };
};

export default useSelect;
